import { Injectable } from '@angular/core';

import { io, Socket } from 'socket.io-client';
import { Observable, Subject, switchMap } from 'rxjs';
import { AuthService } from '@tablesready/frontend-auth';
import { LoggerService } from '@tablesready/frontend-logger';
import { BaseSocketService, getBackEndInstance } from '@tablesready/frontend-store';

declare const process: {
  env: {
    LOCAL_IP: string;
    PORT: string;
    NODE_ENV: string;
    NATIVE_APP_HOST_URL: string;
  };
};

@Injectable({ providedIn: 'root' })
export class SocketsService extends BaseSocketService {
  private onConnectionErrorSubject = new Subject<void>();
  public readonly onConnectionError = this.onConnectionErrorSubject.asObservable();
  private onAuthenticatedSubject = new Subject<void>();
  public readonly onAuthenticated = this.onAuthenticatedSubject.asObservable();

  constructor(
    private authService: AuthService,
    private loggerService: LoggerService,
  ) {
    super();
  }

  getBackendUrl(): string {
    if (process.env.NODE_ENV === 'development' && process.env.LOCAL_IP) {
      return `http://${process.env.LOCAL_IP}:${process.env.PORT}`;
    }

    return getBackEndInstance();
  }

  protected createSocket(): Observable<Socket> {
    return this.authService.getAnonToken().pipe(
      switchMap((token) => {
        return new Observable<Socket>((observer) => {
          const socket = io(this.getBackendUrl(), {
            transports: ['websocket'],
            forceNew: true,
            reconnection: true,
            reconnectionDelay: 500,
            reconnectionAttempts: 10,
            auth: { token: `Bearer ${token}` },
          });

          socket.on('connect', () => {
            this.onAuthenticatedSubject.next();
            observer.next(socket);
          });

          socket.on('error', (error: { type: string }) => {
            this.loggerService.info('Socket connection error:', JSON.stringify(error));
            this.onConnectionErrorSubject.next();
            observer.complete();
          });

          socket.on('reconnect_failed', (error: { type: string }) => {
            this.loggerService.info('Socket reconnection failed after max attempts:', JSON.stringify(error));
            this.onConnectionErrorSubject.next();
            observer.complete();
          });

          socket.on('connection_error', error => {
            this.loggerService.info('Socket connection error:', JSON.stringify(error));
            this.onConnectionErrorSubject.next();
            observer.complete();
          });

          socket.on('disconnect', (reason) => {
            this.loggerService.info('Socket connection disconnect:', reason);
          });
        });
      })
    );
  }
}
